import { Box, ButtonBase, Stack } from '@mui/material';
import { useState, ReactNode } from 'react';
import {
  Copy as CopyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@sweep-io/sweep-design/dist/icons';
import {
  DataTableDraggableColumnProps,
  DataTableDraggableRowProps,
  DataTableRow,
  DataTableSortableProps,
} from './TableTypes';
import { colors } from '@sweep-io/sweep-design';
import { ACTION_BUTTONS_KEY } from './utils';
import { DataTable } from './DataTable';
import ConfirmDestructiveDialog from '../dialogs/ConfirmDestructiveDialog';

export type DataTableOnActionClickClbk = ({
  actionType,
  row,
}: {
  actionType: ActionableDataTableActionTypes;
  row: DataTableRow;
}) => any;

export enum ActionableDataTableActionTypes {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  DUPLICATE = 'DUPLICATE',
}

type ActionableDataTableActionItemType<T = string> = T | ((id: string, row: DataTableRow) => T);

export interface ActionableDataTableAction {
  type: ActionableDataTableActionTypes;
  confirm?: boolean;
  confirmTitle?: ActionableDataTableActionItemType;
  confirmSubtitle?: ActionableDataTableActionItemType;
  confirmButtonCancelLabel?: ActionableDataTableActionItemType;
  confirmButtonOkLabel?: ActionableDataTableActionItemType;
}

const dataTableActionsIconMap = {
  [ActionableDataTableActionTypes.EDIT]: <EditIcon />,
  [ActionableDataTableActionTypes.DELETE]: <DeleteIcon />,
  [ActionableDataTableActionTypes.DUPLICATE]: <CopyIcon />,
};

interface ActionableDataTableOnlyProps {
  actions?: ActionableDataTableAction[];
  onActionClick?: DataTableOnActionClickClbk;
}

export interface ActionableDataTableRow extends DataTableRow {
  disabledActions?: ActionableDataTableActionTypes[];
}

type ActionableDataTableProps<TRow extends ActionableDataTableRow = any> = (
  | DataTableDraggableColumnProps<TRow>
  | DataTableDraggableRowProps<TRow>
  | DataTableSortableProps<TRow>
) &
  ActionableDataTableOnlyProps;

export function ActionableDataTable<TRow extends ActionableDataTableRow = any>({
  rows,
  columns,
  actions,
  onActionClick,
  ...dataTableProps
}: ActionableDataTableProps<TRow>) {
  const [confirmAction, setConfirmAction] = useState<{
    action: ActionableDataTableAction;
    rowId: string;
  }>();

  const hasActions = Boolean(actions?.length);

  const onActionClickWithPossibleConfirmation = ({
    row,
    action,
  }: {
    row: TRow;
    action: ActionableDataTableAction;
  }) => {
    if (action.confirm) {
      setConfirmAction({ action, rowId: row.id });
    } else {
      onActionClick?.({
        actionType: action.type,
        row,
      });
    }
  };

  const ResizedIcon = ({ children }: { children: ReactNode }) => (
    <Box
      sx={{
        svg: {
          width: '16px',
          height: '16px',
        },
        'svg path': {
          stroke: colors.grey[800],
        },
      }}
    >
      {children}
    </Box>
  );

  const rowsWithActions = hasActions
    ? rows.map((row) => ({
        ...row,
        [ACTION_BUTTONS_KEY]: row.disabled ? null : (
          <Stack direction="row" gap={1}>
            {actions
              ?.filter((action) => !row.disabledActions?.includes(action.type))
              .map((action) => (
                <ButtonBase
                  key={action.type}
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    onActionClickWithPossibleConfirmation({
                      action,
                      row,
                    });
                  }}
                >
                  <ResizedIcon>{dataTableActionsIconMap[action.type]}</ResizedIcon>
                </ButtonBase>
              ))}
          </Stack>
        ),
      }))
    : rows;

  const _allowReorder =
    'allowRowReorder' in dataTableProps ? dataTableProps.allowRowReorder : false;
  const chosenRow = rowsWithActions.find((row) => row.id === confirmAction?.rowId);

  const processActionItem = (action?: ActionableDataTableActionItemType<string>) => {
    if (!action || !chosenRow) {
      return;
    }
    if (typeof action === 'function') {
      return action(chosenRow.id, chosenRow);
    } else {
      return action as string;
    }
  };

  return (
    <>
      {confirmAction && (
        <ConfirmDestructiveDialog
          open={!!confirmAction}
          onClose={() => setConfirmAction(undefined)}
          onConfirm={() => {
            setConfirmAction(undefined);

            if (onActionClick && chosenRow) {
              onActionClick({
                actionType: confirmAction.action.type,
                row: chosenRow,
              });
            }
          }}
          confirmText={processActionItem(confirmAction.action.confirmButtonOkLabel) || 'Confirm'}
          cancelText={processActionItem(confirmAction.action.confirmButtonCancelLabel) || 'Cancel'}
          title={processActionItem(confirmAction.action.confirmTitle) || 'Confirm'}
        >
          {processActionItem(confirmAction.action.confirmSubtitle) || 'Are you sure?'}
        </ConfirmDestructiveDialog>
      )}

      <DataTable
        {...dataTableProps}
        allowRowReorder={!!(!confirmAction && _allowReorder)}
        columns={columns}
        rows={rowsWithActions}
        stickyHeader
        sxRowFunction={(isDragging: boolean) => ({
          ...(dataTableProps.sxRowFunction ? dataTableProps.sxRowFunction(isDragging) : {}),
        })}
      />
    </>
  );
}
